import type { CSSProperties, SVGAttributes } from 'react'
import { useId } from 'react'
import styles from './MarqueeRing.module.css'

export interface MarqueeRingProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  /** The phrase to set around the circle. Repeat it yourself to fill the ring. */
  text: string
  /** Rendered width/height in px. */
  size?: number
  /** Seconds for one full rotation. */
  duration?: number
  reverse?: boolean
}

export function MarqueeRing({
  text,
  size = 360,
  duration = 40,
  reverse = false,
  className,
  style,
  ...props
}: MarqueeRingProps) {
  // The textPath reference must be unique per instance, or two rings on one
  // page both follow whichever path rendered first.
  const pathId = useId()
  const center = size / 2
  const radius = center * 0.79

  const classes = [styles.ring, reverse ? styles.reverse : undefined, className]
    .filter(Boolean)
    .join(' ')

  return (
    // The rotation speed rides on this wrapper rather than on the <svg>, and
    // reaches the ring by inheritance — custom properties inherit, so the
    // animation resolves exactly as it did inline.
    //
    // Why it cannot go on the <svg>: setting any inline style there is the one
    // thing that turns a decorative ring into a blank site. React writes inline
    // styles through `element.style`, and a browser that has SVG turned off
    // (Firefox's `svg.disabled`, set by several privacy-hardening profiles)
    // returns a plain Element from createElementNS — no `.style` at all. React
    // throws mid-render, the app's root error boundary catches it, and every
    // visitor on such a profile gets "Something broke" instead of the homepage.
    // Every other prop here is written with setAttribute, which is unaffected.
    <span
      className={styles.wrap}
      style={{ '--ring-duration': `${duration}s` } as CSSProperties}
    >
      <svg
        className={classes}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        // A caller's own style stays on the <svg>, where it has always applied
        // — moving it to a display:contents wrapper would silently drop any
        // positioning they set. It is undefined for every current caller, and
        // React skips `.style` entirely when it is, so the crash is gone either
        // way; passing one is opting into the same risk as any inline-styled svg.
        style={style}
        aria-hidden="true"
        focusable="false"
        {...props}
      >
        <defs>
          <path
            id={pathId}
            fill="none"
            d={`M ${center} ${center} m -${radius} 0 a ${radius} ${radius} 0 1 1 ${radius * 2} 0 a ${radius} ${radius} 0 1 1 -${radius * 2} 0`}
          />
        </defs>
        <text className={styles.text}>
          <textPath href={`#${pathId}`}>{text}</textPath>
        </text>
      </svg>
    </span>
  )
}
